import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { Drawer } from "@circleco/compass/components/Drawer";
import { useEffect, useState } from "react";

import { t } from "@/i18n-js/instance";

import { MobileCreateDrawer } from "../drawers/MobileCreateDrawer";
import { MobileNavDrawerContent } from "../drawers/navDrawer/MobileNavDrawerContent";
import { useMobileCreateAction } from "../hooks/topBar/useMobileCreateAction";
import { useMobileTopBarOverride } from "../hooks/topBar/useMobileTopBarOverride";
import type { MobileTopBarProps } from "../types";
import { MobileLoggedOutTopBar } from "./MobileLoggedOutTopBar";
import { MobileTopBar } from "./MobileTopBar";

/**
 * Renders either:
 * - A full TopBar override (e.g. chat room header with participant info), or
 * - The default TopBar + profile/navigation drawer + create-action drawer.
 *
 * The nav drawer and create drawer are rendered as siblings (not nested)
 * so each gets its own backdrop overlay from base-ui.
 *
 * Owns the create drawer's open state and registers the opener in
 * CreateActionContext (`onCreateOpen`) so pages can trigger it from
 * a page-specific trailing action - e.g. FeedContent's create button.
 * The TopBar itself no longer renders a create button by default; it's a
 * contextual trailing action like any other (see NotificationsTrailingActions).
 *
 * Must be inside MobileSectionTabsProvider to read context.
 */
interface MobileTopBarAreaProps extends MobileTopBarProps {
  isNavOpen: boolean;
  setIsNavOpen: (open: boolean) => void;
}

export const MobileTopBarArea = ({
  isNavOpen,
  setIsNavOpen,
  ...topBarProps
}: MobileTopBarAreaProps) => {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const { topBarOverride } = useMobileTopBarOverride();
  const { setOnCreateOpen } = useMobileCreateAction();
  const { currentUser, currentCommunityMember } = usePunditUserContext();

  useEffect(() => {
    setOnCreateOpen(() => setIsCreateOpen(true));
    return () => setOnCreateOpen(null);
  }, [setOnCreateOpen]);

  // Logged-out or signed-in non-member: show Login/Join buttons instead
  // of profile drawer. Checked before topBarOverride so public pages
  // (e.g. post detail) always show auth actions for unauthenticated users.
  // Without this, signed-in non-members would see an avatar button that
  // opens an empty drawer (MobileNavDrawerContent returns null).
  if (!currentUser || !currentCommunityMember) {
    return (
      <MobileLoggedOutTopBar
        communityName={topBarProps.communityName}
        logoUrl={topBarProps.logoUrl}
      />
    );
  }

  // When a page provides a full TopBar override, render it directly.
  if (topBarOverride) {
    return topBarOverride;
  }

  // Default: community TopBar with profile/navigation drawer.
  // The create drawer is a sibling to the nav drawer so base-ui
  // renders each with its own backdrop overlay.
  return (
    <>
      <Drawer.Root open={isNavOpen} onOpenChange={setIsNavOpen}>
        <MobileTopBar {...topBarProps} />
        <Drawer.Content aria-label={t("mobile_shell.navigation_menu")}>
          <MobileNavDrawerContent onClose={() => setIsNavOpen(false)} />
        </Drawer.Content>
      </Drawer.Root>

      <MobileCreateDrawer open={isCreateOpen} onOpenChange={setIsCreateOpen} />
    </>
  );
};
